import { Tooltip, Typography, type TypographyProps } from '@mui/material';
import { useEffect, useRef, useState } from 'react';

type TruncatedTextProps = TypographyProps & {
  text: string;
  /**
   * How many lines of text to show before truncating with an ellipsis.
   * - 1 (default): single-line truncation via `text-overflow: ellipsis`.
   * - 2+: multi-line truncation via `-webkit-line-clamp`.
   */
  lineClamp?: number;
};

/**
 * A Typography that truncates its text with an ellipsis when it overflows,
 * and shows the full text in a Tooltip on hover/focus only when it is
 * actually truncated. Truncation is detected by comparing the rendered
 * element's scroll size against its visible size.
 */
export const TruncatedText = ({
  text,
  lineClamp = 1,
  sx,
  ...rest
}: TruncatedTextProps) => {
  const ref = useRef<HTMLElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      setIsTruncated(
        lineClamp > 1
          ? el.scrollHeight > el.clientHeight + 1
          : el.scrollWidth > el.clientWidth + 1,
      );
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [text, lineClamp]);

  const clampSx =
    lineClamp > 1
      ? {
          display: '-webkit-box',
          WebkitLineClamp: lineClamp,
          WebkitBoxOrient: 'vertical' as const,
          overflow: 'hidden',
        }
      : {
          whiteSpace: 'nowrap' as const,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        };

  return (
    <Tooltip title={isTruncated ? text : ''} placement="top" arrow>
      <Typography
        ref={ref}
        sx={[clampSx, ...(Array.isArray(sx) ? sx : [sx])]}
        {...rest}
      >
        {text}
      </Typography>
    </Tooltip>
  );
};
